// src/features/subscription/EntitlementCheckDialog.tsx
// Check feature access — the entitlement domain's form-kit dialog
// (docs/ux/14-forms.md), opened from EntitlementListPage's "Check access"
// and from EntitlementDetailPage (prefilled with that entitlement's tenant
// and feature key). Entitlements themselves are read-only projections of
// plans/add-ons — the service exposes no Create/Update endpoints — so the
// only entitlement form contract is CheckEntitlementRequest: tenant,
// feature_key, and an optional product filter. The verdict renders inline
// in the dialog so several keys can be probed for the same tenant without
// reopening it.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { subscriptionApi } from '@core/api';
import { Alert, Badge } from '@react/components';
import { FormDialog, FormRow } from '@/components/form';
import { errMessage, humanizeEnum } from './format';
import type { CheckEntitlementResponse } from '@core/types/subscription/CheckEntitlementResponse';

export interface EntitlementCheckDialogProps {
  open: boolean;
  onClose: () => void;
  /** Prefill for the query fields (e.g. from the current list filter or a detail page). */
  initial?: { tenantId?: string; featureKey?: string; productId?: string };
}

const inputClass =
  'w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring';

export const EntitlementCheckDialog = observer(({ open, onClose, initial }: EntitlementCheckDialogProps) => {
  const { t } = useI18n();

  const [tenantId, setTenantId] = useState(initial?.tenantId ?? '');
  const [featureKey, setFeatureKey] = useState(initial?.featureKey ?? '');
  const [productId, setProductId] = useState(initial?.productId ?? '');
  const [result, setResult] = useState<CheckEntitlementResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTenantId(initial?.tenantId ?? '');
    setFeatureKey(initial?.featureKey ?? '');
    setProductId(initial?.productId ?? '');
    setResult(null);
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = tenantId.trim().length > 0 && featureKey.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || checking) return;
    setChecking(true);
    setError(null);
    setResult(null);
    try {
      const params: Record<string, string> = { feature_key: featureKey.trim() };
      if (productId.trim()) params.product_id = productId.trim();
      const res = await subscriptionApi.CheckEntitlement(tenantId.trim(), { params });
      setResult(res);
    } catch (e) {
      setError(errMessage(e, t('subscription.entitlement.checkError')));
    } finally {
      setChecking(false);
    }
  };

  const ent = result?.entitlement;
  const limit = ent?.limit_value ?? 0;
  const usage = ent?.current_usage ?? 0;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('subscription.entitlement.check')}
      canSubmit={canSubmit}
      isSubmitting={checking}
      aria-label={t('subscription.entitlement.checkTitle')}
    >
      <div className="px-3.5 pb-1 pt-2">
        <h2 className="text-sm font-medium text-foreground">{t('subscription.entitlement.checkTitle')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('subscription.entitlement.checkSubtitle')}</p>
      </div>

      <div className="space-y-1 px-3.5 pb-3 pt-1">
        {error && <Alert variant="error">{error}</Alert>}
        <FormRow label={t('subscription.entitlement.field.featureKey')} labelWidth="sm">
          <input
            value={featureKey}
            onChange={(e) => { setFeatureKey(e.target.value); setResult(null); }}
            placeholder={t('subscription.entitlement.field.featureKeyPlaceholder')}
            aria-label={t('subscription.entitlement.field.featureKey')}
            data-autofocus
            className={inputClass}
          />
        </FormRow>
        <FormRow label={t('subscription.entitlement.field.tenant')} labelWidth="sm">
          <input
            value={tenantId}
            onChange={(e) => { setTenantId(e.target.value); setResult(null); }}
            aria-label={t('subscription.entitlement.field.tenant')}
            className={inputClass}
          />
        </FormRow>
        <FormRow label={t('subscription.entitlement.field.product')} labelWidth="sm" hint={t('subscription.entitlement.field.productHint')}>
          <input
            value={productId}
            onChange={(e) => { setProductId(e.target.value); setResult(null); }}
            aria-label={t('subscription.entitlement.field.product')}
            className={inputClass}
          />
        </FormRow>
      </div>

      {result && (
        <div className="border-t border-border px-3.5 py-3">
          <div className="flex items-center gap-2">
            {result.has_access
              ? <Badge variant="success" size="sm">{t('subscription.entitlement.result.allowed')}</Badge>
              : <Badge variant="error" size="sm">{t('subscription.entitlement.result.denied')}</Badge>}
            <span className="font-mono text-xs text-muted-foreground">{featureKey.trim()}</span>
          </div>

          {!result.has_access && result.denial_reason && (
            <p className="mt-2 text-sm text-destructive">{result.denial_reason}</p>
          )}

          {ent && (
            <dl className="mt-2 space-y-1">
              {ent.type && ent.type !== 'ENTITLEMENT_TYPE_UNSPECIFIED' && (
                <div className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 text-muted-foreground">{t('subscription.entitlement.result.type')}</dt>
                  <dd className="text-foreground">{humanizeEnum(ent.type, 'ENTITLEMENT_TYPE_')}</dd>
                </div>
              )}
              {limit > 0 && (
                <div className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 text-muted-foreground">{t('subscription.entitlement.result.usage')}</dt>
                  <dd className="tabular-nums text-foreground">{usage} / {limit}</dd>
                </div>
              )}
              {result.remaining_usage > 0 && (
                <div className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 text-muted-foreground">{t('subscription.entitlement.result.remaining')}</dt>
                  <dd className="tabular-nums text-foreground">{result.remaining_usage}</dd>
                </div>
              )}
              {ent.source_plan_slug && (
                <div className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 text-muted-foreground">{t('subscription.entitlement.result.sourcePlan')}</dt>
                  <dd className="font-mono text-foreground">{ent.source_plan_slug}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}
    </FormDialog>
  );
});

export default EntitlementCheckDialog;
